/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Immutable Credit Ledger Store (Task 10.6.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export type LedgerType =
  | 'TOPUP_PURCHASE'
  | 'PLAN_ALLOCATION'
  | 'AI_USAGE'
  | 'REFUND'
  | 'ADMIN_ADJUSTMENT'
  | 'BONUS_PROMO';

export type LedgerCategory =
  | 'text'
  | 'chat'
  | 'images'
  | 'video'
  | 'avatar'
  | 'voice'
  | 'translation'
  | 'billing'
  | 'system';

export type TypeFilter = 'all' | 'credit' | 'debit' | 'refund' | 'topup';
export type DateRangeFilter = 'all' | 'today' | '7d' | '30d' | 'month';

export interface LedgerEntry {
  id: string;
  workspaceId: string;
  timestamp: number;
  createdAt: string;
  type: LedgerType;
  category: LedgerCategory;
  amount: number; // Positive (+) for credits grant, negative (-) for deduction
  balanceBefore: number;
  balanceAfter: number;
  description: string;
  modelCode?: string;
  unitsUsed?: string;
  tokensUsed?: number;
  referenceId?: string;
  idempotencyKey?: string;
  clientIp?: string;
  region?: string;
}

/**
 * Generate 40+ chronologically consistent immutable credit ledger entries
 */
function createSeedLedgerEntries(): LedgerEntry[] {
  const entries: LedgerEntry[] = [];
  const now = Date.now();

  const rawEvents: Array<{
    hoursAgo: number;
    type: LedgerType;
    category: LedgerCategory;
    amount: number;
    description: string;
    modelCode?: string;
    unitsUsed?: string;
    tokensUsed?: number;
    referenceId?: string;
  }> = [
    {
      hoursAgo: 1,
      type: 'AI_USAGE',
      category: 'chat',
      amount: -1.5,
      description: 'Conversational assistant session stream with RAG citations',
      modelCode: 'gpt-4o',
      unitsUsed: '1,420 tokens',
      tokensUsed: 1420,
      referenceId: 'chat_sess_8109',
    },
    {
      hoursAgo: 3,
      type: 'AI_USAGE',
      category: 'images',
      amount: -4.0,
      description: 'Ultra HD Flux 1.1 Pro generation: Cyberpunk Neon Studio',
      modelCode: 'flux-1.1-pro',
      unitsUsed: '1 image (1024x1024)',
      tokensUsed: 4000,
      referenceId: 'job_flux_9021',
    },
    {
      hoursAgo: 5,
      type: 'AI_USAGE',
      category: 'voice',
      amount: -3.0,
      description: 'Whisper STT subtitle transcription with word timestamps',
      modelCode: 'whisper-large-v3',
      unitsUsed: '90s audio stream',
      tokensUsed: 1800,
      referenceId: 'stt_whisp_4412',
    },
    {
      hoursAgo: 8,
      type: 'REFUND',
      category: 'video',
      amount: 20.0,
      description: 'Automatic refund: Kling 1.5 Video generation queue timeout',
      modelCode: 'kling-v1.5',
      unitsUsed: '10s video prompt',
      tokensUsed: 0,
      referenceId: 'dlq_refund_7701',
    },
    {
      hoursAgo: 9,
      type: 'AI_USAGE',
      category: 'video',
      amount: -20.0,
      description: 'Cinematic video synthesis request dispatched to queue',
      modelCode: 'kling-v1.5',
      unitsUsed: '10s video prompt',
      tokensUsed: 20000,
      referenceId: 'job_video_7701',
    },
    {
      hoursAgo: 14,
      type: 'AI_USAGE',
      category: 'text',
      amount: -5.0,
      description: 'Longform 4-step SEO article generation (Tech Horizons)',
      modelCode: 'claude-3-5-sonnet',
      unitsUsed: '3,850 words',
      tokensUsed: 5200,
      referenceId: 'doc_art_3391',
    },
    {
      hoursAgo: 18,
      type: 'AI_USAGE',
      category: 'translation',
      amount: -2.0,
      description: 'CAT Translation memory batch auto-translation: 45 segments',
      modelCode: 'deepl-pro',
      unitsUsed: '45 bilingual segments',
      tokensUsed: 2100,
      referenceId: 'cat_proj_1190',
    },
    {
      hoursAgo: 24,
      type: 'AI_USAGE',
      category: 'avatar',
      amount: -30.0,
      description: 'Synthesia AI Talking Avatar keynote presentation video',
      modelCode: 'synthesia-v2',
      unitsUsed: '60s talking head',
      tokensUsed: 30000,
      referenceId: 'job_avatar_5510',
    },
    {
      hoursAgo: 32,
      type: 'AI_USAGE',
      category: 'voice',
      amount: -6.0,
      description: 'ElevenLabs neural speech synthesis: English US Executive',
      modelCode: 'eleven-multilingual-v2',
      unitsUsed: '600 characters',
      tokensUsed: 600,
      referenceId: 'tts_audio_9921',
    },
    {
      hoursAgo: 40,
      type: 'AI_USAGE',
      category: 'images',
      amount: -2.0,
      description: 'Image Ops: 1-click BiRefNet background removal',
      modelCode: 'birefnet-v1',
      unitsUsed: '1 image (transparent PNG)',
      tokensUsed: 2000,
      referenceId: 'img_ops_8820',
    },
    {
      hoursAgo: 50,
      type: 'TOPUP_PURCHASE',
      category: 'billing',
      amount: 10000.0,
      description: 'Pro Token Pack top-up via Stripe Checkout',
      unitsUsed: 'Stripe Credit Card',
      referenceId: 'ord_stripe_9901',
    },
    {
      hoursAgo: 72,
      type: 'AI_USAGE',
      category: 'chat',
      amount: -2.0,
      description: 'DeepSeek R1 reasoning chain analysis on financial report',
      modelCode: 'deepseek-r1',
      unitsUsed: '2,600 reasoning tokens',
      tokensUsed: 2600,
      referenceId: 'chat_sess_8055',
    },
    {
      hoursAgo: 96,
      type: 'ADMIN_ADJUSTMENT',
      category: 'billing',
      amount: 500.0,
      description: 'Admin gesture bonus credits applied for Beta testing feedback',
      unitsUsed: 'Support Ticket #1042',
      referenceId: 'adm_adj_0029',
    },
    {
      hoursAgo: 120,
      type: 'AI_USAGE',
      category: 'images',
      amount: -4.0,
      description: 'Inpainting Canvas generative fill on landscape header',
      modelCode: 'sd-3.5-large',
      unitsUsed: '1 inpaint mask',
      tokensUsed: 4000,
      referenceId: 'inpaint_job_2210',
    },
    {
      hoursAgo: 150,
      type: 'AI_USAGE',
      category: 'text',
      amount: -3.0,
      description: 'Prompt template execution: High-Converting Sales Email',
      modelCode: 'gpt-4o-mini',
      unitsUsed: '1,200 words',
      tokensUsed: 1800,
      referenceId: 'tmpl_exec_9091',
    },
    {
      hoursAgo: 200,
      type: 'PLAN_ALLOCATION',
      category: 'billing',
      amount: 25000.0,
      description: 'Monthly Enterprise Workspace Plan credit quota grant',
      unitsUsed: 'Enterprise Tier Subscription',
      referenceId: 'sub_alloc_2026_10',
    },
  ];

  // Synthesize additional 26 entries backwards in time to total 42 records
  const additionalSubsystems: Array<{ cat: LedgerCategory; model: string; amount: number; units: string }> = [
    { cat: 'chat', model: 'gpt-4o', amount: -1.2, units: '980 tokens' },
    { cat: 'text', model: 'claude-3-5-sonnet', amount: -2.5, units: '1,500 words' },
    { cat: 'images', model: 'dall-e-3', amount: -4.0, units: '1 image (1024x1024)' },
    { cat: 'voice', model: 'azure-neural-tts', amount: -1.0, units: '300 characters' },
    { cat: 'translation', model: 'google-neural-mt', amount: -1.5, units: '30 segments' },
  ];

  for (let i = 0; i < 26; i++) {
    const sub = additionalSubsystems[i % additionalSubsystems.length];
    const isRefund = i === 18;
    const isTopup = i === 22;

    if (isRefund) {
      rawEvents.push({
        hoursAgo: 220 + i * 12,
        type: 'REFUND',
        category: 'images',
        amount: 4.0,
        description: 'Automatic refund: Upstream image generation error',
        modelCode: sub.model,
        unitsUsed: sub.units,
        referenceId: `dlq_refund_${i}`,
      });
    } else if (isTopup) {
      rawEvents.push({
        hoursAgo: 220 + i * 12,
        type: 'TOPUP_PURCHASE',
        category: 'billing',
        amount: 5000.0,
        description: 'One-time token pack top-up via VietQR Dynamic',
        unitsUsed: 'Bank Transfer (VietQR)',
        referenceId: `ord_vqr_${i}`,
      });
    } else {
      rawEvents.push({
        hoursAgo: 220 + i * 12,
        type: 'AI_USAGE',
        category: sub.cat,
        amount: sub.amount,
        description: `Automated ${sub.cat} workflow generation`,
        modelCode: sub.model,
        unitsUsed: sub.units,
        tokensUsed: Math.floor(Math.abs(sub.amount) * 1000),
        referenceId: `ref_sub_${i}`,
      });
    }
  }

  // Sort chronologically ascending from oldest to newest to compute running balance
  rawEvents.sort((a, b) => b.hoursAgo - a.hoursAgo);

  let runningBalance = 0;

  rawEvents.forEach((ev, index) => {
    const balanceBefore = runningBalance;
    runningBalance = Number((runningBalance + ev.amount).toFixed(2));
    const timestamp = now - ev.hoursAgo * 3600000;
    const seqNum = (index + 1).toString().padStart(4, '0');

    entries.push({
      id: `tx_01HP9${seqNum}`,
      workspaceId: 'ws_demo_primary',
      timestamp,
      createdAt: new Date(timestamp).toISOString(),
      type: ev.type,
      category: ev.category,
      amount: ev.amount,
      balanceBefore,
      balanceAfter: runningBalance,
      description: ev.description,
      modelCode: ev.modelCode,
      unitsUsed: ev.unitsUsed,
      tokensUsed: ev.tokensUsed,
      referenceId: ev.referenceId || `ref_${seqNum}`,
      idempotencyKey: `idemp_${crypto.randomUUID().slice(0, 12)}`,
      clientIp: `113.161.${index % 250}.${(index * 7) % 250}`,
      region: index % 2 === 0 ? 'SIN - Singapore Edge' : 'HAN - Hanoi Edge',
    });
  });

  // Return sorted descending (newest first) for user table
  return entries.reverse();
}

export const SEED_LEDGER_ENTRIES: LedgerEntry[] = createSeedLedgerEntries();

export interface CreditLedgerState {
  entries: LedgerEntry[];
  searchQuery: string;
  typeFilter: TypeFilter;
  categoryFilter: 'all' | LedgerCategory;
  dateRange: DateRangeFilter;
  selectedEntry: LedgerEntry | null;
  isDetailModalOpen: boolean;

  // Actions
  setEntries: (entries: LedgerEntry[]) => void;
  setSearchQuery: (searchQuery: string) => void;
  setTypeFilter: (typeFilter: TypeFilter) => void;
  setCategoryFilter: (categoryFilter: 'all' | LedgerCategory) => void;
  setDateRange: (dateRange: DateRangeFilter) => void;
  setSelectedEntry: (selectedEntry: LedgerEntry | null) => void;
  setDetailModalOpen: (isDetailModalOpen: boolean) => void;
  openDetailModal: (entry: LedgerEntry) => void;
  getFilteredEntries: () => LedgerEntry[];
  getLedgerStats: () => {
    currentBalance: number;
    totalDeposited: number;
    totalConsumed: number;
    totalRefunded: number;
    transactionCount: number;
  };
  exportLedgerAsCsv: () => string;
}

export const useCreditLedgerStore = create<CreditLedgerState>((set, get) => ({
  entries: [],
  searchQuery: '',
  typeFilter: 'all',
  categoryFilter: 'all',
  dateRange: 'all',
  selectedEntry: null,
  isDetailModalOpen: false,

  setEntries: (entries) => set({ entries }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setTypeFilter: (typeFilter) => set({ typeFilter }),
  setCategoryFilter: (categoryFilter) => set({ categoryFilter }),
  setDateRange: (dateRange) => set({ dateRange }),
  setSelectedEntry: (selectedEntry) => set({ selectedEntry }),
  setDetailModalOpen: (isDetailModalOpen) => set({ isDetailModalOpen }),

  openDetailModal: (entry) => {
    set({ selectedEntry: entry, isDetailModalOpen: true });
  },

  getFilteredEntries: () => {
    const { entries, searchQuery, typeFilter, categoryFilter, dateRange } = get();
    const query = searchQuery.trim().toLowerCase();
    const now = Date.now();

    return entries.filter((entry) => {
      // Type filter
      if (typeFilter === 'debit' && entry.amount >= 0) return false;
      if (typeFilter === 'credit' && entry.amount <= 0) return false;
      if (typeFilter === 'refund' && entry.type !== 'REFUND') return false;
      if (typeFilter === 'topup' && entry.type !== 'TOPUP_PURCHASE' && entry.type !== 'PLAN_ALLOCATION') return false;

      // Category filter
      if (categoryFilter !== 'all' && entry.category !== categoryFilter) return false;

      // Date Range filter
      if (dateRange === 'today') {
        const startOfDay = new Date().setHours(0, 0, 0, 0);
        if (entry.timestamp < startOfDay) return false;
      } else if (dateRange === '7d') {
        if (entry.timestamp < now - 7 * 86400000) return false;
      } else if (dateRange === '30d') {
        if (entry.timestamp < now - 30 * 86400000) return false;
      } else if (dateRange === 'month') {
        const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
        if (entry.timestamp < startOfMonth) return false;
      }

      // Search query filter
      if (query) {
        const matchesId = entry.id.toLowerCase().includes(query);
        const matchesDesc = entry.description.toLowerCase().includes(query);
        const matchesModel = entry.modelCode?.toLowerCase().includes(query) ?? false;
        const matchesRef = entry.referenceId?.toLowerCase().includes(query) ?? false;
        if (!matchesId && !matchesDesc && !matchesModel && !matchesRef) {
          return false;
        }
      }

      return true;
    });
  },

  getLedgerStats: () => {
    const { entries } = get();
    if (entries.length === 0) {
      return {
        currentBalance: 0,
        totalDeposited: 0,
        totalConsumed: 0,
        totalRefunded: 0,
        transactionCount: 0,
      };
    }

    // Latest entry has current balanceAfter
    const currentBalance = entries[0].balanceAfter;

    let totalDeposited = 0;
    let totalConsumed = 0;
    let totalRefunded = 0;

    entries.forEach((e) => {
      if (e.type === 'TOPUP_PURCHASE' || e.type === 'PLAN_ALLOCATION' || e.type === 'BONUS_PROMO' || e.type === 'ADMIN_ADJUSTMENT' && e.amount > 0) {
        totalDeposited += e.amount;
      } else if (e.type === 'REFUND') {
        totalRefunded += e.amount;
      } else if (e.amount < 0) {
        totalConsumed += Math.abs(e.amount);
      }
    });

    return {
      currentBalance,
      totalDeposited: Number(totalDeposited.toFixed(2)),
      totalConsumed: Number(totalConsumed.toFixed(2)),
      totalRefunded: Number(totalRefunded.toFixed(2)),
      transactionCount: entries.length,
    };
  },

  exportLedgerAsCsv: () => {
    const { getFilteredEntries } = get();
    const filtered = getFilteredEntries();

    const headers = [
      'Transaction ID',
      'Timestamp (ISO)',
      'Type',
      'Category',
      'Model Code',
      'Units Used',
      'Credit Amount',
      'Balance Before',
      'Balance After',
      'Reference ID',
      'Description',
      'Client IP',
      'Edge Region',
      'Idempotency Key',
    ];

    const rows = filtered.map((e) => [
      e.id,
      new Date(e.timestamp).toISOString(),
      e.type,
      e.category,
      e.modelCode || '',
      `"${(e.unitsUsed || '').replace(/"/g, '""')}"`,
      e.amount.toString(),
      e.balanceBefore.toString(),
      e.balanceAfter.toString(),
      e.referenceId || '',
      `"${e.description.replace(/"/g, '""')}"`,
      e.clientIp || '',
      `"${(e.region || '').replace(/"/g, '""')}"`,
      e.idempotencyKey || '',
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    return csvContent;
  },
}));
